import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useContactFilesQuery } from '../../../../../../queries';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../../components/DataGrid';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Placeholder } from '../../../../../components/Placeholder';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { Button } from '../../../../../components-v2/Button';
import { Pill } from '../../../../../components-v2/Pill';
import { styleSheet } from './styles';

function formatFileSize(bytes: number): string {
	if (bytes >= 1_000_000) {
		return `${(bytes / 1_000_000).toFixed(1)} MB`;
	}
	if (bytes >= 1_000) {
		return `${Math.round(bytes / 1_000)} KB`;
	}
	return `${bytes} B`;
}

function formatDate(dateStr?: string): string {
	if (!dateStr) {
		return '';
	}
	return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function getUploaderName(creator?: Api.UserReference): string {
	if (!creator) {
		return '';
	}
	return creator.fullName ?? [creator.firstName, creator.lastName].filter(Boolean).join(' ');
}

type IProps = {
	contactId?: string;
};

export const ContactFiles = ({ contactId }: IProps) => {
	const userSession = useUserSession();
	const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

	const filesQuery = useContactFilesQuery({
		contactId: contactId ?? '',
		enabled: !!contactId,
	});

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = filesQuery;

	const files = useMemo(
		() => filesQuery.data?.pages.flatMap((page: Api.IPagedCollection<Api.ISharedFile>) => page.values) ?? [],
		[filesQuery.data]
	);

	const handleDownload = async (file: Api.ISharedFile) => {
		setDownloadingFileId(file.id);
		try {
			const attachment = await userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `attachment/${file.fileAttachment.id}` }),
				'GET'
			);
			if (attachment.url) {
				const link = document.createElement('a');
				link.href = attachment.url;
				link.download = attachment.fileName;
				link.click();
				link.remove();
			}
		} catch {
			toast.error('Failed to download file');
		} finally {
			setDownloadingFileId(null);
		}
	};

	if (!contactId) {
		return;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.content)}>
				<Placeholder
					isLoading={filesQuery.isPending}
					isEmpty={!filesQuery.isPending && files.length === 0}
					message='No files have been shared with this contact yet.'
				/>
				{!filesQuery.isPending && files.length > 0 && (
					<DataGrid styles={[styleSheet.dataGrid]}>
						<DataGridColumns>
							<DataGridColumn>Filename</DataGridColumn>
							<DataGridColumn>Date</DataGridColumn>
							<DataGridColumn>Source</DataGridColumn>
							<DataGridColumn>Uploader</DataGridColumn>
							<DataGridColumn>Size</DataGridColumn>
						</DataGridColumns>
						<DataGridRows>
							{files.map((file: Api.ISharedFile) => {
								const isDownloading = downloadingFileId === file.id;
								return (
									<DataGridRow key={file.id}>
										<DataGridRowItem>
											<div className={css(styleSheet.filenameCell)}>
												<FileTextIcon width={16} height={16} />
												<Button
													variant='link'
													disabled={isDownloading}
													onClick={() => handleDownload(file)}
													title='Download'
												>
													{file.fileAttachment.fileName}
												</Button>
											</div>
										</DataGridRowItem>
										<DataGridRowItem>{formatDate(file.creationDate)}</DataGridRowItem>
										<DataGridRowItem>
											<Pill variant='label' color='gray' label={file.source.sourceType} size='small' />
										</DataGridRowItem>
										<DataGridRowItem>{getUploaderName(file.creator)}</DataGridRowItem>
										<DataGridRowItem>{formatFileSize(file.fileAttachment.fileSize)}</DataGridRowItem>
									</DataGridRow>
								);
							})}
						</DataGridRows>
					</DataGrid>
				)}
				{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />}
				{isFetchingNextPage && <LoadingSpinner type='extra-tiny' />}
			</div>
		</div>
	);
};
